import React from 'react';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { CheckIcon } from '@assets/icons';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { AccommodationCard } from '../Accommodations.types';

type RemoveSilentlyFn = (hotel: AccommodationCard, opts: { silent: true }) => Promise<void>;

type BulkRemoveArgs = Readonly<{
  hotels: ReadonlyArray<AccommodationCard>;
  /** Called after the batch settles with only the IDs that succeeded — failed hotels stay selected for retry. */
  onSettled: (succeededIds: ReadonlyArray<string>) => void;
}>;

type UseBulkRemoveHotelsReturn = Readonly<{
  bulkRemove: (args: BulkRemoveArgs) => Promise<void>;
}>;

export const useBulkRemoveHotels = (removeSilently: RemoveSilentlyFn): UseBulkRemoveHotelsReturn => {
  const { tq } = useTranslation('accommodationsAdmin');
  const { toast } = useToast();

  const bulkRemove = useEventCallback(async ({ hotels, onSettled }: BulkRemoveArgs) => {
    const results = await Promise.allSettled(hotels.map(h => removeSilently(h, { silent: true })));
    const succeededIds = results.flatMap((r, i) => (r.status === 'fulfilled' ? [hotels[i].id] : []));
    onSettled(succeededIds);

    const failures = results.length - succeededIds.length;
    if (failures === 0) {
      toast(succeededIds.length === 1 ? tq`Hotel removed.` : tq`Hotels removed.`, { icon: <CheckIcon /> });
    } else if (succeededIds.length === 0) {
      toast(tq`Couldn't delete hotels. Try again.`, { icon: <WarningTriangle /> });
    } else {
      toast(tq`Some hotels couldn't be deleted. Try again.`, { icon: <WarningTriangle /> });
    }
  });

  return { bulkRemove };
};
